import { useRef, useCallback, useState, useEffect } from 'react';
import userAvailabilityService from '../services/userAvailabilityService';
import { getEmailValidationError } from '../utils/emailValidation';
import {
  isUsernameFormatValid,
  sanitizeUsernameInput,
} from '../utils/usernameValidation';

export const USERNAME_TAKEN_MESSAGE = 'This username is already taken. Please choose a different username.';
export const EMAIL_TAKEN_MESSAGE = 'This email address is already registered. Please use a different email.';
export const SSN_TAKEN_MESSAGE = 'This SSN is already registered to another patient. Please use a different SSN.';

const TAKEN_MESSAGES = new Set([
  USERNAME_TAKEN_MESSAGE,
  EMAIL_TAKEN_MESSAGE,
  SSN_TAKEN_MESSAGE,
  'Username is already in use.',
  'Email address is already in use.',
  'SSN is already in use.',
  'Admin username is already in use.',
  'Admin email address is already in use.',
]);

function isTakenMessage(msg) {
  if (!msg || typeof msg !== 'string') return false;
  if (TAKEN_MESSAGES.has(msg)) return true;
  const lower = msg.toLowerCase();
  return (
    (lower.includes('username') || lower.includes('email') || lower.includes('ssn'))
    && (lower.includes('taken') || lower.includes('already') || lower.includes('in use') || lower.includes('registered'))
  );
}

function normalizeOptions(excludeUserIdOrOptions) {
  if (
    excludeUserIdOrOptions
    && typeof excludeUserIdOrOptions === 'object'
    && (
      'excludeUserId' in excludeUserIdOrOptions
      || 'excludePatientId' in excludeUserIdOrOptions
      || 'originalUsername' in excludeUserIdOrOptions
      || 'originalEmail' in excludeUserIdOrOptions
      || 'originalSsn' in excludeUserIdOrOptions
    )
  ) {
    return excludeUserIdOrOptions;
  }
  return { excludeUserId: excludeUserIdOrOptions ?? null };
}

function normalizeSsnDigits(value) {
  return String(value ?? '').replace(/\D/g, '');
}

function isEmailFormatValid(value) {
  return getEmailValidationError(value) === null;
}

function isSsnFormatValid(value) {
  return normalizeSsnDigits(value).length === 9;
}

function isSameCredentialValue(current, original) {
  const saved = String(original ?? '').trim().toLowerCase();
  const next = String(current ?? '').trim().toLowerCase();
  return saved !== '' && next === saved;
}

function isSameSsnValue(current, original) {
  const saved = normalizeSsnDigits(original);
  const next = normalizeSsnDigits(current);
  return saved !== '' && next === saved;
}

function parseAvailabilityResponse(response) {
  const body = response?.data;
  if (!body || body.success !== true) return null;
  return {
    available: Boolean(body.available),
    message: body.message || '',
  };
}

/**
 * Debounced API checks for username/email/SSN uniqueness.
 */
export default function useUsernameEmailAvailability(excludeUserIdOrOptions = null, debounceMs = 500) {
  const {
    excludeUserId,
    excludePatientId,
    originalUsername,
    originalEmail,
    originalSsn,
  } = normalizeOptions(excludeUserIdOrOptions);
  const timersRef = useRef({});
  const seqRef = useRef({ username: 0, email: 0, ssn: 0 });
  // Always read latest exclude/originals when a debounced check fires so edit forms
  // do not mark the current user's own username/email as "taken" after a race.
  const optionsRef = useRef({
    excludeUserId,
    excludePatientId,
    originalUsername,
    originalEmail,
    originalSsn,
  });
  optionsRef.current = {
    excludeUserId,
    excludePatientId,
    originalUsername,
    originalEmail,
    originalSsn,
  };
  const [checking, setChecking] = useState({ username: false, email: false, ssn: false });

  const clearTimer = useCallback((field) => {
    if (timersRef.current[field]) {
      clearTimeout(timersRef.current[field]);
      delete timersRef.current[field];
    }
  }, []);

  useEffect(() => () => {
    Object.values(timersRef.current).forEach((timerId) => clearTimeout(timerId));
  }, []);

  // When edit credentials become available, drop any in-flight checks that ran too early.
  useEffect(() => {
    seqRef.current.username += 1;
    seqRef.current.email += 1;
    seqRef.current.ssn += 1;
    clearTimer('username');
    clearTimer('email');
    clearTimer('ssn');
    setChecking((prev) => ({ ...prev, username: false, email: false, ssn: false }));
  }, [excludeUserId, excludePatientId, originalUsername, originalEmail, originalSsn, clearTimer]);

  const clearAvailabilityError = useCallback((setErrors, errorKey) => {
    setErrors((prev) => {
      if (!isTakenMessage(prev?.[errorKey])) return prev;
      const next = { ...prev };
      delete next[errorKey];
      return next;
    });
  }, []);

  const applyAvailabilityResult = useCallback((setErrors, errorKey, available, message) => {
    console.log(`[useUsernameEmailAvailability] applyAvailabilityResult for '${errorKey}':`, { available, message });
    setErrors((prev) => {
      const next = { ...prev };
      if (!available) {
        const fallback = errorKey.includes('email')
          ? EMAIL_TAKEN_MESSAGE
          : errorKey.includes('ssn')
            ? SSN_TAKEN_MESSAGE
            : USERNAME_TAKEN_MESSAGE;
        next[errorKey] = message || fallback;
      } else {
        delete next[errorKey];
      }
      console.log(`[useUsernameEmailAvailability] Errors state updated for '${errorKey}'. New errors:`, next);
      return next;
    });
  }, []);

  const shouldSkipUsernameCheck = useCallback((value) => (
    isSameCredentialValue(value, optionsRef.current.originalUsername)
  ), []);

  const shouldSkipEmailCheck = useCallback((value) => (
    isSameCredentialValue(value, optionsRef.current.originalEmail)
  ), []);

  const shouldSkipSsnCheck = useCallback((value) => (
    isSameSsnValue(value, optionsRef.current.originalSsn)
  ), []);

  const runCheck = useCallback(async (field, value, setErrors, errorKey) => {
    const opts = optionsRef.current;
    console.log(`[useUsernameEmailAvailability] runCheck starting for '${field}':`, { value, opts });

    if (field === 'username' && isSameCredentialValue(value, opts.originalUsername)) {
      console.log(`[useUsernameEmailAvailability] Skipping ${field} check: matches original value`);
      clearAvailabilityError(setErrors, errorKey);
      return true;
    }
    if (field === 'email' && isSameCredentialValue(value, opts.originalEmail)) {
      console.log(`[useUsernameEmailAvailability] Skipping ${field} check: matches original value`);
      clearAvailabilityError(setErrors, errorKey);
      return true;
    }
    if (field === 'ssn' && isSameSsnValue(value, opts.originalSsn)) {
      console.log(`[useUsernameEmailAvailability] Skipping ${field} check: matches original value`);
      clearAvailabilityError(setErrors, errorKey);
      return true;
    }

    const seq = ++seqRef.current[field];
    setChecking((prev) => ({ ...prev, [field]: true }));

    try {
      let response;
      if (field === 'username') {
        response = await userAvailabilityService.checkUsername(value, opts.excludeUserId, opts.excludePatientId);
      } else if (field === 'email') {
        response = await userAvailabilityService.checkEmail(value, opts.excludeUserId, opts.excludePatientId);
      } else {
        response = await userAvailabilityService.checkSsn(value, opts.excludePatientId, opts.excludeUserId);
      }

      if (seq !== seqRef.current[field]) return null;

      // Credentials may have loaded while the request was in flight.
      const latest = optionsRef.current;
      if (field === 'username' && isSameCredentialValue(value, latest.originalUsername)) {
        clearAvailabilityError(setErrors, errorKey);
        return true;
      }
      if (field === 'email' && isSameCredentialValue(value, latest.originalEmail)) {
        clearAvailabilityError(setErrors, errorKey);
        return true;
      }
      if (field === 'ssn' && isSameSsnValue(value, latest.originalSsn)) {
        clearAvailabilityError(setErrors, errorKey);
        return true;
      }

      const parsed = parseAvailabilityResponse(response);
      console.log(`[useUsernameEmailAvailability] runCheck response for '${field}':`, parsed);
      if (parsed) {
        if (field === 'username' && !isUsernameFormatValid(value)) {
          clearAvailabilityError(setErrors, errorKey);
          return true;
        }
        if (field === 'email' && !isEmailFormatValid(value)) {
          clearAvailabilityError(setErrors, errorKey);
          return true;
        }
        if (field === 'ssn' && !isSsnFormatValid(value)) {
          clearAvailabilityError(setErrors, errorKey);
          return true;
        }
        applyAvailabilityResult(setErrors, errorKey, parsed.available, parsed.message);
        return parsed.available;
      }
      return null;
    } catch (error) {
      console.warn(`Availability check failed for ${field}:`, error?.message || error);
      return null;
    } finally {
      if (seq === seqRef.current[field]) {
        setChecking((prev) => ({ ...prev, [field]: false }));
      }
    }
  }, [applyAvailabilityResult, clearAvailabilityError]);

  const scheduleUsernameCheck = useCallback((value, setErrors, errorKey = 'username', options = {}) => {
    clearTimer('username');
    if (!isUsernameFormatValid(value)) return;

    if (shouldSkipUsernameCheck(value)) {
      clearAvailabilityError(setErrors, errorKey);
      return;
    }

    const delay = options?.immediate ? 0 : debounceMs;
    timersRef.current.username = setTimeout(() => {
      // Re-evaluate skip with latest originals right before calling the API.
      if (shouldSkipUsernameCheck(value)) {
        clearAvailabilityError(setErrors, errorKey);
        return;
      }
      runCheck('username', String(value).trim(), setErrors, errorKey);
    }, delay);
  }, [clearTimer, runCheck, debounceMs, shouldSkipUsernameCheck, clearAvailabilityError]);

  /**
   * Paste does not always behave like keystrokes for debounced checks.
   * Read the post-paste value and force an immediate uniqueness check.
   */
  const handleUsernamePaste = useCallback((event, setErrors, errorKey = 'username') => {
    const input = event?.target;
    const pastedText = event?.clipboardData?.getData('text') ?? '';
    const currentValue = String(input?.value ?? '');
    const start = input?.selectionStart ?? currentValue.length;
    const end = input?.selectionEnd ?? currentValue.length;
    const nextRaw = `${currentValue.slice(0, start)}${pastedText}${currentValue.slice(end)}`;
    const cleaned = sanitizeUsernameInput(nextRaw);

    // After React onChange/sanitize runs, flush an immediate availability check.
    window.setTimeout(() => {
      if (cleaned && isUsernameFormatValid(cleaned)) {
        scheduleUsernameCheck(cleaned, setErrors, errorKey, { immediate: true });
      }
    }, 0);
  }, [scheduleUsernameCheck]);

  const handleUsernameBlur = useCallback((valueOrEvent, setErrors, errorKey = 'username') => {
    const rawValue = (valueOrEvent && typeof valueOrEvent === 'object' && 'target' in valueOrEvent)
      ? valueOrEvent.target.value
      : valueOrEvent;
    const cleaned = sanitizeUsernameInput(rawValue);
    if (cleaned && isUsernameFormatValid(cleaned)) {
      scheduleUsernameCheck(cleaned, setErrors, errorKey, { immediate: true });
    }
  }, [scheduleUsernameCheck]);

  const scheduleEmailCheck = useCallback((value, setErrors, errorKey = 'email', options = {}) => {
    clearTimer('email');
    if (!isEmailFormatValid(value)) return;

    if (shouldSkipEmailCheck(value)) {
      clearAvailabilityError(setErrors, errorKey);
      return;
    }

    const delay = options?.immediate ? 0 : debounceMs;
    timersRef.current.email = setTimeout(() => {
      if (shouldSkipEmailCheck(value)) {
        clearAvailabilityError(setErrors, errorKey);
        return;
      }
      runCheck('email', String(value).trim(), setErrors, errorKey);
    }, delay);
  }, [clearTimer, runCheck, debounceMs, shouldSkipEmailCheck, clearAvailabilityError]);

  const handleEmailBlur = useCallback((valueOrEvent, setErrors, errorKey = 'email') => {
    const rawValue = (valueOrEvent && typeof valueOrEvent === 'object' && 'target' in valueOrEvent)
      ? valueOrEvent.target.value
      : valueOrEvent;
    const cleaned = String(rawValue ?? '').trim();
    if (cleaned && isEmailFormatValid(cleaned)) {
      scheduleEmailCheck(cleaned, setErrors, errorKey, { immediate: true });
    }
  }, [scheduleEmailCheck]);

  const handleEmailPaste = useCallback((event, setErrors, errorKey = 'email') => {
    const input = event?.target;
    const pastedText = event?.clipboardData?.getData('text') ?? '';
    const currentValue = String(input?.value ?? '');
    const start = input?.selectionStart ?? currentValue.length;
    const end = input?.selectionEnd ?? currentValue.length;
    const nextRaw = `${currentValue.slice(0, start)}${pastedText}${currentValue.slice(end)}`.trim();

    window.setTimeout(() => {
      if (nextRaw && isEmailFormatValid(nextRaw)) {
        scheduleEmailCheck(nextRaw, setErrors, errorKey, { immediate: true });
      }
    }, 0);
  }, [scheduleEmailCheck]);

  const scheduleSsnCheck = useCallback((value, setErrors, errorKey = 'ssn') => {
    clearTimer('ssn');
    if (!isSsnFormatValid(value)) {
      clearAvailabilityError(setErrors, errorKey);
      return;
    }

    if (shouldSkipSsnCheck(value)) {
      clearAvailabilityError(setErrors, errorKey);
      return;
    }

    timersRef.current.ssn = setTimeout(() => {
      runCheck('ssn', normalizeSsnDigits(value), setErrors, errorKey);
    }, debounceMs);
  }, [clearTimer, runCheck, debounceMs, shouldSkipSsnCheck, clearAvailabilityError]);

  const ensureUsernameAvailable = useCallback(async (value, setErrors, errorKey = 'username') => {
    clearTimer('username');
    if (!isUsernameFormatValid(value)) return true;
    if (shouldSkipUsernameCheck(value)) {
      clearAvailabilityError(setErrors, errorKey);
      return true;
    }
    const available = await runCheck('username', String(value).trim(), setErrors, errorKey);
    return available !== false;
  }, [clearTimer, runCheck, shouldSkipUsernameCheck, clearAvailabilityError]);

  const ensureEmailAvailable = useCallback(async (value, setErrors, errorKey = 'email') => {
    clearTimer('email');
    if (!isEmailFormatValid(value)) return true;
    if (shouldSkipEmailCheck(value)) {
      clearAvailabilityError(setErrors, errorKey);
      return true;
    }
    const available = await runCheck('email', String(value).trim(), setErrors, errorKey);
    return available !== false;
  }, [clearTimer, runCheck, shouldSkipEmailCheck, clearAvailabilityError]);

  const ensureSsnAvailable = useCallback(async (value, setErrors, errorKey = 'ssn') => {
    clearTimer('ssn');
    if (!isSsnFormatValid(value)) return true;
    if (shouldSkipSsnCheck(value)) {
      clearAvailabilityError(setErrors, errorKey);
      return true;
    }
    const available = await runCheck('ssn', normalizeSsnDigits(value), setErrors, errorKey);
    return available !== false;
  }, [clearTimer, runCheck, shouldSkipSsnCheck, clearAvailabilityError]);

  return {
    scheduleUsernameCheck,
    scheduleEmailCheck,
    scheduleSsnCheck,
    handleUsernamePaste,
    handleUsernameBlur,
    handleEmailPaste,
    handleEmailBlur,
    ensureUsernameAvailable,
    ensureEmailAvailable,
    ensureSsnAvailable,
    checking,
  };
}
